
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, profile } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const navbarClasses = cn(
    "fixed top-0 left-0 right-0 z-50 py-4 px-6 transition-all duration-300", 
    scrolled ? "bg-gradient-to-b from-[#0EA5E9] to-[#3B82F6] shadow-md" : "bg-transparent"
  );

  // Determine dashboard link based on user role
  const getDashboardLink = () => {
    if (!profile?.role) return "/auth";
    
    return profile.role === 'job_seeker' 
      ? "/job-seeker-dashboard" 
      : "/recruiter-dashboard";
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className={navbarClasses}>
      <div className="container mx-auto flex justify-between items-center">
        <Link to="/" className={scrolled ? "text-white font-bold text-xl" : "text-[#0EA5E9] font-bold text-xl"}>Talento AI</Link>
        
        {/* Mobile menu toggle */}
        <button 
          className="lg:hidden z-50"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? (
            <X className={scrolled ? "text-white" : "text-[#0EA5E9]"} />
          ) : (
            <Menu className={scrolled ? "text-white" : "text-[#0EA5E9]"} />
          )}
        </button>

        {/* Mobile Menu */}
        <div className={`lg:hidden fixed inset-0 bg-white/95 dark:bg-black/95 z-40 flex flex-col items-center justify-center transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <nav className="flex flex-col items-center gap-6 text-lg">
            <Link to="/" className="text-[#0EA5E9] hover:text-[#0EA5E9]/80" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <div className="relative group">
              <button className="text-[#0EA5E9] hover:text-[#0EA5E9]/80 flex items-center gap-1">
                Services
              </button>
              <div className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-56 opacity-0 group-focus-within:opacity-100 invisible group-focus-within:visible transition-all">
                <Link to="/talent-recruitment" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>Talent Recruitment</Link>
                <Link to="/ai-agent-development" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>AI Agent Development</Link>
                <Link to="/sop-automation" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>SOP Automation</Link>
                <Link to="/ai-training" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>AI Training</Link>
              </div>
            </div>
            <Link to="/about" className="text-[#0EA5E9] hover:text-[#0EA5E9]/80" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" className="text-[#0EA5E9] hover:text-[#0EA5E9]/80" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            {user ? (
              <Link to={getDashboardLink()} onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-[#FEF7CD] text-black hover:bg-[#FEF7CD]/90 w-full">Dashboard</Button>
              </Link>
            ) : (
              <Link to="/join-waitlist" onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-[#FEF7CD] text-black hover:bg-[#FEF7CD]/90 w-full">Join Waitlist</Button>
              </Link>
            )}
          </nav>
        </div>
        
        {/* Desktop Navigation */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList>
            <NavigationMenuItem>
              <Link to="/" className={scrolled ? "text-white hover:text-white/80 px-3 py-2" : "text-[#0EA5E9] hover:text-[#0EA5E9]/80 px-3 py-2"}>Home</Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <NavigationMenuTrigger className={scrolled ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-[#0EA5E9] hover:text-[#0EA5E9]/80 bg-transparent hover:bg-[#0EA5E9]/10"}>Services</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/talent-recruitment" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium">Talent Recruitment</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Find the right talent for your AI-augmented teams</p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/ai-agent-development" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium">AI Agent Development</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Custom AI agents for your business needs</p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/sop-automation" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium">SOP Automation</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Transform manual procedures into automated workflows</p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/ai-training" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                        <div className="text-sm font-medium">AI Training</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Prepare your team to work with AI effectively</p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <Link to="/about" className={scrolled ? "text-white hover:text-white/80 px-3 py-2" : "text-[#0EA5E9] hover:text-[#0EA5E9]/80 px-3 py-2"}>About</Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <Link to="/contact" className={scrolled ? "text-white hover:text-white/80 px-3 py-2" : "text-[#0EA5E9] hover:text-[#0EA5E9]/80 px-3 py-2"}>Contact</Link>
            </NavigationMenuItem>
            
            {user && (
              <NavigationMenuItem>
                <Link to={getDashboardLink()}>
                  <Button className="bg-[#FEF7CD] text-black hover:bg-[#FEF7CD]/90">Dashboard</Button>
                </Link>
              </NavigationMenuItem>
            )}
            
            {!user && (
              <NavigationMenuItem>
                <Link to="/join-waitlist">
                  <Button className="bg-[#FEF7CD] text-black hover:bg-[#FEF7CD]/90">Join Waitlist</Button>
                </Link>
              </NavigationMenuItem>
            )}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    </header>
  );
};

export default Navbar;
